import { PLACE_HOLDER_IMAGE } from "@/src/shared/constants/app";
import type { ICampaignListing, IListing } from "@/src/shared/types/listing";
import type {
  IBoostAttribution,
  ISponsoredListing,
  ISponsoredPagination,
  ISponsoredQuery,
} from "./types";

/** The app hides the home row until there are at least this many boosted stays. */
export const FEATURED_MIN_TO_SHOW = 3;
/** Cards on the home row; the rest are on the rail's page. */
export const FEATURED_HOME_LIMIT = 7;
/** A view-all page loads this many at a time (the API allows up to 50). */
export const FEATURED_PAGE_SIZE = 20;
/** The app's fallback title (the API sends none). */
export const FEATURED_TITLE = "Featured Stays";

/** Query keys a listing page reads, and removes once the click is sent. */
const BOOST_QUERY_KEYS = ["boost_id", "from_page"] as const;

/**
 * `from_page` for a boosted click: `web_featured_stays_` + where the card was, so the
 * stats say which page it came from; a view-all page (rail page, drawer, panel) adds
 * `_details`. All underscores, like the app's names (`featured_stays_home`).
 */
const placement = (where: string, details: boolean) =>
  `web_featured_stays_${where.replace(/-/g, "_")}${details ? "_details" : ""}`;
export const BOOST_FROM_PAGE = {
  /** The home row of a tab: `web_featured_stays_rooms`. */
  home: (tabSlug: string, details = false) => placement(tabSlug, details),
  /** The search page's row: `web_featured_stays_search`. */
  search: (details = false) => placement("search", details),
  /** The bottom sheet a phone gets on the search page. */
  searchPopup: placement("search_popup", false),
};

/** The search params the boost API also takes (the rest are search-only). */
const SEARCH_KEYS = [
  "location",
  "location_id",
  "from",
  "to",
  "guest",
  "child",
  "price",
  "place_types",
  "property_type_ids",
  "ranks",
  "instant_booking",
  "min_rating",
  "filters",
] as const;

const pad = (n: number) => String(n).padStart(2, "0");
const isoDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const single = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

export const boostService = {
  /** A home tab's boost campaign, as the app maps it (`_campaignSlugFor`). */
  campaignSlugForTab(tabSlug: string): string | null {
    switch (tabSlug.trim().toLowerCase()) {
      case "apartments":
      case "homes":
        return "homes";
      case "rooms":
        return "rooms";
      case "hotels":
        return "hotels";
      default:
        return null;
    }
  },

  /** The home window: today to the day after tomorrow, so prices are for two nights. */
  homeQuery(campaignSlug: string, today: Date): ISponsoredQuery {
    const to = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 2,
    );
    return { campaign_slug: campaignSlug, from: isoDay(today), to: isoDay(to) };
  },

  /** The search page's boosted query: the live search, minus what the boost API ignores. */
  searchQuery(
    params: Record<string, string | number | boolean | undefined>,
  ): ISponsoredQuery {
    const query: ISponsoredQuery = {};
    for (const key of SEARCH_KEYS) {
      const value = params[key];
      if (value !== undefined && value !== "") query[key] = value;
    }
    return query;
  },

  /**
   * The search's Featured Stays page (`/search/featured-stays?…`), which the search row's
   * title opens on desktop: every boosted stay for the same search.
   */
  searchPageHref(query: ISponsoredQuery): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      params.set(key, String(value));
    const search = params.toString();
    return search
      ? `/search/featured-stays?${search}`
      : "/search/featured-stays";
  },

  /** That page's query back from its URL: only the search keys, first value of each. */
  searchQueryFromUrl(
    urlQuery: Record<string, string | string[] | undefined>,
  ): ISponsoredQuery {
    const flat: Record<string, string> = {};
    for (const [key, value] of Object.entries(urlQuery)) {
      const first = Array.isArray(value) ? value[0] : value;
      if (first !== undefined) flat[key] = first;
    }
    return boostService.searchQuery(flat);
  },

  /** A search, ignoring filters and page: the app re-shows its popup only for a new one. */
  searchIdentity(query: ISponsoredQuery): string {
    return ["location", "location_id", "from", "to", "guest", "child"]
      .map((key) => String(query[key] ?? ""))
      .join("|");
  },

  /**
   * How many cards a row of `width` px shows at once: as many as fit at `minCardWidth`
   * with `gap` between them, from `min` up to `max`. Sized by the row, not the window,
   * because on the search page the filter sidebar takes part of the width.
   */
  cardsInView(
    width: number,
    {
      min,
      max,
      minCardWidth,
      gap,
    }: {
      min: number;
      max: number;
      minCardWidth: number;
      gap: number;
    },
  ): number {
    const fit = Math.floor((width + gap) / (minCardWidth + gap));
    return Math.min(max, Math.max(min, fit));
  },

  /** The next page to fetch, or undefined after the last. */
  nextPage(pagination: ISponsoredPagination | null): number | undefined {
    if (!pagination) return undefined;
    return pagination.page < pagination.total_pages
      ? pagination.page + 1
      : undefined;
  },

  /** A boosted listing as `ListingCard` takes it: its cover photo and the nightly price. */
  toCardListing(item: ISponsoredListing): ICampaignListing {
    // One photo per card: the cover, i.e. the lowest priority (0, else 1, …). A second
    // photo only added weight to every Featured Stays row.
    const cover = [...item.images].sort((a, b) => a.priority - b.priority)[0];
    const images = [
      cover ?? { id: item.id, url: PLACE_HOLDER_IMAGE, priority: 0 },
    ];
    return {
      id: item.id,
      listing: {
        id: item.id,
        title: item.title,
        price: item.average_price || item.price,
        reviews_avg: item.reviews_avg ?? 0,
        reviews_count: item.reviews_count,
        is_hotel: item.is_hotel,
        hotel_id: item.hotel_id,
        place_type: item.place_type ?? "",
        property_type: item.property_type,
        images,
      } as unknown as IListing,
    } as ICampaignListing;
  },

  /** What a card adds to the listing link so the page can report the click. */
  linkQuery(item: ISponsoredListing, fromPage: string): Record<string, string> {
    return { boost_id: String(item.boost_id), from_page: fromPage };
  },

  /** The attribution a listing page was opened with, if it came from a boosted card. */
  readAttribution(
    query: Record<string, string | string[] | undefined>,
  ): IBoostAttribution | null {
    const boostId = Number(single(query.boost_id));
    const fromPage = single(query.from_page)?.trim() ?? "";
    if (!Number.isInteger(boostId) || boostId <= 0 || !fromPage) return null;
    return { boostId, fromPage };
  },

  /** A URL (path + query) without the boost params, other params untouched. */
  stripAttribution(url: string): string {
    const [path = "", search = ""] = url.split("?");
    const params = new URLSearchParams(search);
    for (const key of BOOST_QUERY_KEYS) params.delete(key);
    const rest = params.toString();
    return rest ? `${path}?${rest}` : path;
  },
};
